/**
 * VideoTutorialSectionV2.tsx — prop-driven Video Tutorial Section.
 *
 * Path B (satie-gymnopedie migration, 2026-05-23): parallel to VideoTutorialSection
 * in BlogPostPageClient.tsx. Keeps the Für Elise code path completely untouched —
 * zero regression surface. Same architectural decision as NumberedTimeline.tsx.
 *
 * Visual identity: visually IDENTICAL to the Für Elise VideoTutorialSection at
 * every breakpoint (375 / 768 / 1024 / 1440). All .vts3-* CSS rules in globals.css
 * apply directly — no new CSS introduced.
 *
 *   - Eyebrow: "VIDEO TUTORIAL" (centered, cream-surface, .vts3-eyebrow)
 *   - H2: italic serif via `heading` prop (.vts3-h2)
 *   - H3: "What do you learn in the video?" (hardcoded, matches V1)
 *   - Italic intro paragraph via `intro` prop (.vts3-intro, wrapped in <em>)
 *   - 3 icon items via children — rendered from <videoTutorialSectionV2Item> nodes
 *   - Gold S-curve SVG connector (.vts3-connector)
 *
 * MDX usage (child-component syntax — NOT inline JSX prop arrays):
 *
 *   <videoTutorialSectionV2 heading="How to play Gymnopédie No. 1" intro="...">
 *     <videoTutorialSectionV2Item icon="technique-serves-expression" title="Piano Fingering...">
 *       Body text here.
 *     </videoTutorialSectionV2Item>
 *     <videoTutorialSectionV2Item icon="listen-before-play" title="Use of the Sustain Pedal...">
 *       Body text here.
 *     </videoTutorialSectionV2Item>
 *     <videoTutorialSectionV2Item icon="performer-interpretation" title="Character of the Music">
 *       Body text here.
 *     </videoTutorialSectionV2Item>
 *   </videoTutorialSectionV2>
 *
 * Architecture matches NumberedTimeline / numberedTimelineItem pattern exactly:
 * - Parent receives `children` (Tina rich-text AST with videoTutorialSectionV2Item nodes)
 * - Inner TinaMarkdown call uses videoTutorialSectionV2ItemComponents map
 * - Item component gets `icon` + `title` as string props, body as `children`
 *
 * CSS: all inherited from globals.css .vts3-* rules. No new rules.
 * Icons: from components/blog/PhilosophyIcon.tsx — inline SVG, no <img>.
 * Current consumers: how-to-play-erik-satie-gymnopedie-1 (1 post).
 */

// TinaMarkdown removed (Cycle 76-A — Tina stripping)
import PhilosophyIcon, { type PhilosophyIconName } from './PhilosophyIcon';

// ---------------------------------------------------------------------------
// VideoTutorialSectionV2Item — single item inside the section
// ---------------------------------------------------------------------------

interface VideoTutorialSectionV2ItemProps {
  icon: PhilosophyIconName;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export function VideoTutorialSectionV2Item({
  icon,
  title,
  children,
}: VideoTutorialSectionV2ItemProps) {
  return (
    <div className="vts3-item">
      {/*
        Icon slot: bare on cream. NO tile, NO border, NO border-radius,
        NO background. Mirrors .vts3-item__icon exactly.
        64px desktop / 48px mobile — CSS in globals.css handles responsive.
      */}
      <div className="vts3-item__icon" aria-hidden="true">
        <PhilosophyIcon name={icon} width={64} height={64} />
      </div>
      <div className="vts3-item__content">
        <h3 className="vts3-item__title">{title}</h3>
        <div className="vts3-item__body">
          {null /* TinaMarkdown removed — Cycle 76-A; body rendered via dangerouslySetInnerHTML in page */}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VideoTutorialSectionV2 — parent component
// ---------------------------------------------------------------------------

interface VideoTutorialSectionV2Props {
  /** Italic serif H2 — e.g. "How to play Gymnopédie No. 1" */
  heading: string;
  /** Italic intro paragraph (rendered inside <em>) */
  intro: string;
  /** Tina rich-text AST containing videoTutorialSectionV2Item nodes */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

// videoTutorialSectionV2ItemComponents — inner component map for the
// TinaMarkdown call that renders the children AST.
//
// Defined here (in module scope) so it can be imported by BlogPostPageClient.tsx
// and passed as `components` to the inner TinaMarkdown call.
// Pattern matches numberedTimelineItemComponents exactly.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const videoTutorialSectionV2ItemComponents: Record<string, any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  videoTutorialSectionV2Item: ({ icon, title, children }: { icon: PhilosophyIconName; title: string; children?: any }) => (
    <VideoTutorialSectionV2Item icon={icon} title={title}>
      {children}
    </VideoTutorialSectionV2Item>
  ),
};

export default function VideoTutorialSectionV2({
  heading,
  intro,
  children,
}: VideoTutorialSectionV2Props) {
  return (
    <section
      className="video-tutorial-section vts3-section"
      aria-labelledby="vts3v2-heading"
      data-tina-field-overlay
    >
      <div className="vts3-container">
        {/* ── Section header: eyebrow + H2 ── */}
        <div className="vts3-header">
          {/*
            Eyebrow: --eyebrow-on-cream (#7D6B3B) = 4.9:1 on cream — WCAG AA PASS.
            NOT --gold (#D4B86E) which fails at 2.7:1 on cream (text use).
            aria-hidden: true matches V1 pattern.
          */}
          <span
            className="eyebrow eyebrow--centered eyebrow--on-cream vts3-eyebrow"
            aria-hidden="true"
          >
            VIDEO TUTORIAL
          </span>
          <h2 id="vts3v2-heading" className="vts3-h2">
            {heading}
          </h2>
        </div>

        {/* ── Single column timeline — matches V1 structure ── */}
        <div className="vts3-timeline">
          <h3 className="vts3-content-h3">What do you learn in the video?</h3>
          <p className="vts3-intro">
            <em>{intro}</em>
          </p>

          {/* ── Timeline items with S-curve connector ── */}
          <div className="vts3-timeline-items">
            {/*
              Gold S-curve SVG connector — identical to V1 (lines 361-376 of
              BlogPostPageClient.tsx). viewBox="0 0 64 260", exact path values
              preserved. Decorative only — aria-hidden.
            */}
            <svg
              className="vts3-connector"
              aria-hidden="true"
              viewBox="0 0 64 260"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M 32 32 C 14 80 50 100 32 160 C 14 218 50 220 32 228"
                stroke="#D4B86E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>

            {/*
              Children are rendered via an outer TinaMarkdown call in the
              BlogPostPageClient markdownComponents map, passing
              videoTutorialSectionV2ItemComponents so each child item
              renders correctly. See BlogPostPageClient.tsx videoTutorialSectionV2
              mapping and videoTutorialSectionV2ItemComponents above.
            */}
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
